import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import apiService from '../services/api'
import { ShoppingBag, Calendar, Package, User, Eye, XCircle, ClipboardList, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatDate } from '../utils/dateUtils'

const ClientReservations = () => {
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [selectedReservation, setSelectedReservation] = useState(null)
  const [showProductsModal, setShowProductsModal] = useState(false)
  const navigate = useNavigate()

  // Charger les paramètres admin
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => apiService.get('/rentman/settings/public', { params: { t: Date.now() } }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000
    }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const showPrice = backendShowPrice === true

  useEffect(() => {
    const token = localStorage.getItem('user_token')
    if (!token) {
      navigate(`/login?redirect=${encodeURIComponent('/account/reservations')}`)
      return
    }
    
    loadReservations()
  }, [])

  const loadReservations = async () => {
    try {
      setLoading(true)
      const res = await apiService.get('/reservations/my')
      setReservations(res.data.rentman_reservations || [])
    } catch (error) {
      console.error('Erreur chargement réservations:', error)
      toast.error('Erreur lors du chargement des réservations')
    } finally {
      setLoading(false)
    }
  }

  const refreshReservations = async () => {
    setIsRefreshing(true)
    try {
      await loadReservations()
      toast.success('Statuts mis à jour')
    } catch (error) {
      console.error('Erreur rafraîchissement:', error)
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setIsRefreshing(false)
    }
  }

  const getStatusBadge = (status, rentmanStatus) => {
    const styles = {
      demande_envoyee: 'bg-orange-100 text-orange-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      refused: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800',
      open: 'bg-yellow-100 text-yellow-800'
    }
    const labels = {
      demande_envoyee: 'Demande envoyée',
      confirmed: 'Devis envoyé',
      cancelled: 'Annulée',
      completed: 'Terminée',
      pending: 'En attente',
      accepted: 'Acceptée',
      declined: 'Refusée',
      refused: 'Refusée',
      rejected: 'Refusée',
      open: 'Ouverte'
    }
    
    // Utiliser le statut local mappé (status) qui est déjà synchronisé depuis Rentman
    // Le statut local reflète déjà le statut Rentman après le mapping
    const displayStatus = status
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[displayStatus] || 'bg-gray-100 text-gray-800'}`}>
        {labels[displayStatus] || displayStatus}
      </span>
    )
  }


  const showProducts = (reservation) => {
    setSelectedReservation(reservation)
    setShowProductsModal(true)
  }

  const closeProductsModal = () => {
    setShowProductsModal(false)
    setSelectedReservation(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container-custom py-8">
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <ShoppingBag className="h-8 w-8 text-orange-500 mr-3" />
            Mes Réservations
          </h1>
          <p className="mt-2 text-gray-600">
            Consultez l'historique de vos réservations et suivez leur statut
          </p>
            </div>
            <button
              onClick={refreshReservations}
              disabled={isRefreshing || loading}
              className="btn-outline flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation gauche */}
          <div className="lg:col-span-1">
            <div className="card">
              <nav className="space-y-2">
                <Link 
                  to="/account"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                >
                  <User className="h-4 w-4 mr-2" />
                  Coordonnées
                </Link>
                <Link 
                  to="/account/reservations"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm bg-orange-50 text-orange-600 border border-orange-100 shadow-sm"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Mes réservations
                </Link>
                <Link 
                  to="/account/favorites"
                  className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Mes listes
                </Link>
              </nav>
            </div>
          </div>

          {/* Contenu droite */}
          <div className="lg:col-span-3">
            {reservations.length === 0 ? (
              <div className="card text-center py-12">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune réservation</h3>
                <p className="text-gray-600 mb-6">Vous n'avez pas encore effectué de réservation</p>
                <button
                  onClick={() => navigate('/catalogue-materiels')}
                  className="btn-primary"
                >
                  Parcourir le catalogue
                </button>
              </div>
            ) : (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          N°
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date de demande
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Période
                        </th>
                        {showPrice && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Détails
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reservations.map((reservation) => (
                        <tr key={reservation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{reservation.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(reservation.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                              {formatDate(reservation.start_date)} - {formatDate(reservation.end_date)}
                            </div>
                          </td>
                          {showPrice && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {reservation.total_price ? `${reservation.total_price}€` : '-'}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(reservation.status, reservation.rentman_status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <button
                              onClick={() => showProducts(reservation)}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal des produits */}
        {showProductsModal && selectedReservation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Produits - Réservation #{selectedReservation.id}
                  </h3>
                  <button
                    onClick={closeProductsModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  {selectedReservation.products && Array.isArray(selectedReservation.products) ? (
                    selectedReservation.products.map((product, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="mb-1">
                          <span className="font-medium text-gray-900">{product.name || 'Produit'}</span>
                          <div className="text-sm text-gray-600 mt-1">Quantité: {product.quantity || 1}</div>
                        </div>
                        {adminSettingsData?.show_regular_price && product.price_per_day && (
                          <div className="text-sm text-gray-600">
                            Prix/jour: {product.price_per_day}€
                          </div>
                        )}
                        {adminSettingsData?.show_regular_price && product.total_price && (
                          <div className="text-sm font-medium text-orange-600">
                            Sous-total: {product.total_price}€
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <Package className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-gray-900">{selectedReservation.produit_name || 'Produit'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {adminSettingsData?.show_regular_price && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Total:</span>
                      <span className="text-lg font-bold text-orange-600">
                        {selectedReservation.total_price ? `${selectedReservation.total_price}€` : '-'}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <button
                    onClick={closeProductsModal}
                    className="w-full btn-primary"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientReservations
